import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { SuccessResponse } from '../shared/success-response';
import { FindNotificationsQueryDto } from './dtos/find-notifications.query.dto';
import { FindNotificationsResponseDto } from './dtos/find-notifications.response.dto';
import { FindNotificationResponseDto } from './dtos/find-notification.response.dto';
import { BaseUpdatedDto } from '../dto/base-updated.dto';
import { MarkNotificationsAsReadRequestDto } from './dtos/mark-notifications-as-read.request.dto';
import { MarkNotificationsAsUnreadRequestDto } from './dtos/mark-notifications-as-unread.request.dto';
import { DeleteNotificationsRequestDto } from './dtos/delete-notifications.request.dto';

@Injectable({
  providedIn: 'root',
})
export class NotificationsApiService {
  private http = inject(HttpClient);
  private readonly API_URL = `${environment.api}/notifications`;

  async findNotifications(
    query: FindNotificationsQueryDto
  ): Promise<FindNotificationsResponseDto> {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindNotificationsResponseDto>>(
        this.API_URL,
        {
          params,
        }
      )
    );

    return response.data;
  }

  async findNotification(
    notificationId: number
  ): Promise<FindNotificationResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindNotificationResponseDto>>(
        `${this.API_URL}/${notificationId}`
      )
    );

    return response.data;
  }

  async markAllNotificationsAsRead(): Promise<BaseUpdatedDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<BaseUpdatedDto>>(
        `${this.API_URL}/read-all`,
        {}
      )
    );

    return response.data;
  }

  async markNotificationsAsRead(
    dto: MarkNotificationsAsReadRequestDto
  ): Promise<BaseUpdatedDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<BaseUpdatedDto>>(
        `${this.API_URL}/read`,
        dto
      )
    );

    return response.data;
  }

  async markNotificationsAsUnread(
    dto: MarkNotificationsAsUnreadRequestDto
  ): Promise<BaseUpdatedDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<BaseUpdatedDto>>(
        `${this.API_URL}/unread`,
        dto
      )
    );

    return response.data;
  }

  async markNotificationAsRead(
    notificationId: number
  ): Promise<BaseUpdatedDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<BaseUpdatedDto>>(
        `${this.API_URL}/${notificationId}/read`,
        {}
      )
    );

    return response.data;
  }

  async markNotificationAsUnread(
    notificationId: number
  ): Promise<BaseUpdatedDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<BaseUpdatedDto>>(
        `${this.API_URL}/${notificationId}/unread`,
        {}
      )
    );

    return response.data;
  }

  async deleteNotification(
    notificationId: number
  ): Promise<BaseUpdatedDto> {
    const response = await firstValueFrom(
      this.http.delete<SuccessResponse<BaseUpdatedDto>>(
        `${this.API_URL}/${notificationId}`,
        {}
      )
    );

    return response.data;
  }

  async deleteNotifications(
    dto: DeleteNotificationsRequestDto
  ): Promise<BaseUpdatedDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<BaseUpdatedDto>>(
        `${this.API_URL}/delete-many`,
        dto
      )
    );

    return response.data;
  }
}
